/**
 * SpoofIO Validation Schemas
 *
 * Zod schemas for validating all API inputs
 */

import { z } from 'zod'
import {
  MAX_CANARY_NAME_LENGTH,
  MAX_CANARY_DESCRIPTION_LENGTH,
  MAX_AUTHORIZED_DOMAINS_PER_CANARY,
  MIN_CONFIDENCE_THRESHOLD,
  MAX_CONFIDENCE_THRESHOLD,
  DEFAULT_CONFIDENCE_THRESHOLD,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  PIXEL_TOKEN_REGEX,
  EMAIL_PROVIDERS,
} from '../config/constants'
import { EMAIL_TEMPLATE_IDS } from '../templates/registry/types'

// ============================================================================
// Common Validators
// ============================================================================

/**
 * Valid email address
 */
export const emailSchema = z
  .string()
  .email('Invalid email address')
  .max(254, 'Email address too long')
  .transform((email) => email.toLowerCase().trim())

/**
 * Valid FQDN (Fully Qualified Domain Name)
 * - No protocol (http://, https://)
 * - No wildcards (*.example.com)
 * - No trailing slash
 * - Case insensitive
 */
/** Allowed characters in a DNS label */
const DNS_LABEL_CHARS = /^[a-z0-9-]+$/i

/** Validate a single DNS label */
function isValidDnsLabel(label: string): boolean {
  if (label.length === 0 || label.length > 63) return false
  if (label.startsWith('-') || label.endsWith('-')) return false
  return DNS_LABEL_CHARS.test(label)
}

const TLD_REGEX = /^[a-z]{2,}$/i

function isValidFqdn(val: string): boolean {
  const labels = val.split('.')
  if (labels.length < 2) return false
  const tld = labels[labels.length - 1]
  if (!tld || !TLD_REGEX.test(tld)) return false
  return labels.slice(0, -1).every(isValidDnsLabel)
}

export const fqdnSchema = z
  .string()
  .min(1, 'Domain name is required')
  .max(253, 'Domain name too long')
  .refine(isValidFqdn, {
    message: 'Invalid domain name format. Use format: example.com (no protocol or trailing slash)',
  })
  .transform((fqdn) => fqdn.toLowerCase().trim())

/**
 * UUID format
 */
export const uuidSchema = z.string().uuid('Invalid UUID format')

/**
 * Canary token format
 */
export const tokenSchema = z
  .string()
  .min(1, 'Token is required')
  .max(100, 'Token too long')
  .regex(PIXEL_TOKEN_REGEX, 'Invalid token format. Only alphanumeric, hyphens, and underscores allowed')

/**
 * Confidence threshold (0-100)
 */
export const confidenceThresholdSchema = z
  .number()
  .int('Threshold must be an integer')
  .min(MIN_CONFIDENCE_THRESHOLD, `Threshold must be at least ${MIN_CONFIDENCE_THRESHOLD}`)
  .max(MAX_CONFIDENCE_THRESHOLD, `Threshold must be at most ${MAX_CONFIDENCE_THRESHOLD}`)

/**
 * ISO 8601 date string
 */
export const isoDateSchema = z.string().datetime({ message: 'Invalid date format. Use ISO 8601 format.' })

// ============================================================================
// Organization Schemas
// ============================================================================

export const createOrganizationSchema = z.object({
  name: z
    .string()
    .min(1, 'Organization name is required')
    .max(100, 'Organization name too long'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(50, 'Slug too long')
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
      'Slug must be lowercase alphanumeric with hyphens (e.g., my-organization)'
    )
    .refine((val) => !val.includes('--'), {
      message: 'Slug must not contain consecutive hyphens',
    }),
})

export const updateOrganizationSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  graylogEndpoint: z.string().url('Invalid Graylog endpoint URL').optional().nullable(),
  graylogApiKey: z.string().min(1).max(500).optional().nullable(),
})

// ============================================================================
// Canary Schemas
// ============================================================================

export const createCanarySchema = z.object({
  name: z
    .string()
    .min(1, 'Canary name is required')
    .max(MAX_CANARY_NAME_LENGTH, `Name must be ${MAX_CANARY_NAME_LENGTH} characters or less`),
  description: z
    .string()
    .max(MAX_CANARY_DESCRIPTION_LENGTH, `Description must be ${MAX_CANARY_DESCRIPTION_LENGTH} characters or less`)
    .optional(),
  authorizedDomains: z
    .array(fqdnSchema)
    .min(1, 'At least one authorized domain is required')
    .max(
      MAX_AUTHORIZED_DOMAINS_PER_CANARY,
      `Maximum ${MAX_AUTHORIZED_DOMAINS_PER_CANARY} authorized domains allowed`
    ),
  screenshotEnabled: z.boolean().default(false),
  confidenceThreshold: confidenceThresholdSchema.default(DEFAULT_CONFIDENCE_THRESHOLD),
})

export const updateCanarySchema = z.object({
  name: z.string().min(1).max(MAX_CANARY_NAME_LENGTH).optional(),
  description: z.string().max(MAX_CANARY_DESCRIPTION_LENGTH).optional().nullable(),
  screenshotEnabled: z.boolean().optional(),
  confidenceThreshold: confidenceThresholdSchema.optional(),
  isActive: z.boolean().optional(),
})

// ============================================================================
// Authorized Domain Schemas
// ============================================================================

export const addAuthorizedDomainSchema = z.object({
  fqdn: fqdnSchema,
  isDevelopment: z.boolean().default(false),
})

export const updateAuthorizedDomainSchema = z.object({
  fqdn: fqdnSchema,
})

// ============================================================================
// Recipient Schemas
// ============================================================================

export const addRecipientSchema = z.object({
  email: emailSchema,
})

// ============================================================================
// Detection Query Schemas
// ============================================================================

export const detectionQuerySchema = z.object({
  canaryId: z.string().min(1).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  minConfidence: z.coerce.number().int().min(MIN_CONFIDENCE_THRESHOLD).max(MAX_CONFIDENCE_THRESHOLD).optional(),
  maxConfidence: z.coerce.number().int().min(MIN_CONFIDENCE_THRESHOLD).max(MAX_CONFIDENCE_THRESHOLD).optional(),
  fakeFqdn: z.string().min(1).max(253).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
  sortBy: z.enum(['detected_at', 'confidence_score']).default('detected_at'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

// ============================================================================
// Pagination Schemas
// ============================================================================

export const paginationSchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

// ============================================================================
// Type Exports
// ============================================================================

export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>
export type CreateCanaryInput = z.infer<typeof createCanarySchema>
export type UpdateCanaryInput = z.infer<typeof updateCanarySchema>
export type AddAuthorizedDomainInput = z.infer<typeof addAuthorizedDomainSchema>
export type UpdateAuthorizedDomainInput = z.infer<typeof updateAuthorizedDomainSchema>
export type AddRecipientInput = z.infer<typeof addRecipientSchema>
export type DetectionQueryInput = z.infer<typeof detectionQuerySchema>
export type PaginationInput = z.infer<typeof paginationSchema>

// ============================================================================
// Billing Schemas
// ============================================================================

export const createCheckoutSessionSchema = z.object({
  planType: z.enum(['base', 'custom']),
  successUrl: z.string().url('Invalid success URL'),
  cancelUrl: z.string().url('Invalid cancel URL'),
})

export const createPortalSessionSchema = z.object({
  returnUrl: z.string().url('Invalid return URL'),
  flow: z
    .enum(['payment_method_update', 'subscription_cancel', 'subscription_update'])
    .optional(),
})

export const updatePlanAddonsSchema = z.object({
  additionalDomains: z.number().int().min(0).max(100).optional(),
  additionalCanaries: z.number().int().min(0).max(500).optional(),
})

export const addInvoiceRecipientSchema = z.object({
  email: emailSchema,
})

export const usageQuerySchema = z.object({
  canaryId: z.string().min(1).optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format').optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format').optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const acceptTermsSchema = z.object({
  termsVersion: z.string().min(1, 'Terms version is required').max(20),
})

export const updateUserProfileSchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  phone: z.string().min(1).max(30).optional().nullable(),
  mobilePhone: z.string().min(1).max(30).optional().nullable(),
  receiveNotifications: z.boolean().optional(),
})

/**
 * Onboarding wizard completion. Endpoint takes no request body — this schema
 * exists so the handler can validate {} uniformly with other mutating routes.
 */
export const completeOnboardingSchema = z.object({}).strict()

// ============================================================================
// Platform Admin Schemas
// ============================================================================

export const adminListOrganizationsSchema = z.object({
  search: z.string().max(200).optional(),
  status: z.string().max(50).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const adminListUsersSchema = z.object({
  search: z.string().max(200).optional(),
  organizationId: z.string().max(100).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const adminListCanariesSchema = z.object({
  search: z.string().max(200).optional(),
  organizationId: z.string().max(100).optional(),
  isActive: z.coerce.boolean().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const adminListDetectionsSchema = z.object({
  organizationId: z.string().max(100).optional(),
  canaryId: z.string().max(100).optional(),
  dateFrom: z.string().max(40).optional(),
  dateTo: z.string().max(40).optional(),
  minConfidence: z.coerce.number().int().min(MIN_CONFIDENCE_THRESHOLD).max(MAX_CONFIDENCE_THRESHOLD).optional(),
  maxConfidence: z.coerce.number().int().min(MIN_CONFIDENCE_THRESHOLD).max(MAX_CONFIDENCE_THRESHOLD).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const adminAuditLogSchema = z.object({
  organizationId: z.string().max(100).optional(),
  action: z.string().max(100).optional(),
  userEmail: z.string().max(254).optional(),
  dateFrom: z.string().max(40).optional(),
  dateTo: z.string().max(40).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const adminDetectionTrendsSchema = z.object({
  organizationId: z.string().max(100).optional(),
  dateFrom: z.string().max(40).optional(),
  dateTo: z.string().max(40).optional(),
  groupBy: z.enum(['day', 'hour']).default('day'),
})

export const adminAlertStatsSchema = z.object({
  dateFrom: z.string().max(40).optional(),
  dateTo: z.string().max(40).optional(),
})

export type AdminListOrganizationsInput = z.infer<typeof adminListOrganizationsSchema>
export type AdminListUsersInput = z.infer<typeof adminListUsersSchema>
export type AdminListCanariesInput = z.infer<typeof adminListCanariesSchema>
export type AdminListDetectionsInput = z.infer<typeof adminListDetectionsSchema>
export type AdminAuditLogInput = z.infer<typeof adminAuditLogSchema>
export type AdminDetectionTrendsInput = z.infer<typeof adminDetectionTrendsSchema>
export type AdminAlertStatsInput = z.infer<typeof adminAlertStatsSchema>

export const adminIntegrationErrorsSchema = z.object({
  service: z.enum(['clerk', 'stripe', 'sendgrid', 'graylog']).optional(),
  worker: z.enum(['admin-api', 'alert-worker', 'billing-worker']).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})
export type AdminIntegrationErrorsInput = z.infer<typeof adminIntegrationErrorsSchema>

// ============================================================================
// Platform Admin Mutation Schemas
// ============================================================================

export const adminUpdateOrganizationSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  slug: z
    .string()
    .min(1)
    .max(50)
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
      'Slug must be lowercase alphanumeric with hyphens'
    )
    .refine((val) => !val.includes('--'), {
      message: 'Slug must not contain consecutive hyphens',
    })
    .optional(),
  graylogEndpoint: z.string().url('Invalid Graylog endpoint URL').optional().nullable(),
  graylogApiKey: z.string().min(1).max(500).optional().nullable(),
})

export const adminExtendTrialSchema = z.object({
  days: z.number().int().min(1).max(365),
})

export const adminSuspendSchema = z.object({
  reason: z.string().max(500).optional(),
})

export const adminUpdateUserSchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  phone: z.string().min(1).max(30).optional().nullable(),
  mobilePhone: z.string().min(1).max(30).optional().nullable(),
})

export const adminResendAlertSchema = z.object({
  recipients: z.array(emailSchema).optional(),
})

export type AdminUpdateOrganizationInput = z.infer<typeof adminUpdateOrganizationSchema>
export type AdminExtendTrialInput = z.infer<typeof adminExtendTrialSchema>
export type AdminSuspendInput = z.infer<typeof adminSuspendSchema>
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>
export type AdminResendAlertInput = z.infer<typeof adminResendAlertSchema>

export const adminCreateOrganizationSchema = z.object({
  name: z.string().min(1).max(100),
  slug: z
    .string()
    .min(1)
    .max(50)
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
      'Slug must be lowercase alphanumeric with hyphens'
    )
    .refine((val) => !val.includes('--'), {
      message: 'Slug must not contain consecutive hyphens',
    }),
  ownerEmail: z.string().email().optional(),
  activateImmediately: z.boolean().default(false),
})

export type AdminCreateOrganizationInput = z.infer<typeof adminCreateOrganizationSchema>

export const adminCreateUserSchema = z.object({
  email: z.string().email(),
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
})

export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>

export const adminAddMemberSchema = z.object({
  email: z.string().email(),
  role: z.enum(['owner', 'admin', 'member']),
})

export const adminUpdateMemberRoleSchema = z.object({
  role: z.enum(['owner', 'admin', 'member']),
})

export type AdminAddMemberInput = z.infer<typeof adminAddMemberSchema>
export type AdminUpdateMemberRoleInput = z.infer<typeof adminUpdateMemberRoleSchema>

// ============================================================================
// Platform Admin Canary Schemas
// ============================================================================

export const adminCreateCanarySchema = z.object({
  organizationId: z.string().min(1),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  authorizedDomains: z.array(fqdnSchema).min(1).max(50),
  screenshotEnabled: z.boolean().default(false),
  confidenceThreshold: z.number().int().min(0).max(100).default(70),
})

export type AdminCreateCanaryInput = z.infer<typeof adminCreateCanarySchema>

export const adminUpdateCanarySchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  screenshotEnabled: z.boolean().optional(),
  confidenceThreshold: z.number().int().min(0).max(100).optional(),
  isActive: z.boolean().optional(),
})

export type AdminUpdateCanaryInput = z.infer<typeof adminUpdateCanarySchema>

// ============================================================================
// Platform Admin Allowlist Schemas
// ============================================================================

export const createAllowlistEntrySchema = z.object({
  signalType: z.enum(['referrer', 'user_agent', 'ip_range']),
  pattern: z.string().min(1).max(1000),
  description: z.string().min(1).max(500),
})

export const updateAllowlistEntrySchema = z.object({
  signalType: z.enum(['referrer', 'user_agent', 'ip_range']).optional(),
  pattern: z.string().min(1).max(1000).optional(),
  description: z.string().min(1).max(500).optional(),
  isActive: z.boolean().optional(),
})

export const adminListAllowlistSchema = z.object({
  signalType: z.enum(['referrer', 'user_agent', 'ip_range']).optional(),
  isActive: z.coerce.boolean().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export type CreateAllowlistEntryInput = z.infer<typeof createAllowlistEntrySchema>
export type UpdateAllowlistEntryInput = z.infer<typeof updateAllowlistEntrySchema>
export type AdminListAllowlistInput = z.infer<typeof adminListAllowlistSchema>

// ============================================================================
// Platform Admin Notification Schemas
// ============================================================================

export const adminListNotificationsSchema = z.object({
  type: z.string().max(100).optional(),
  severity: z.enum(['info', 'warning', 'critical']).optional(),
  isRead: z.coerce.boolean().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
})

export const createNotificationSchema = z.object({
  type: z.string().min(1).max(100),
  title: z.string().min(1).max(200),
  message: z.string().min(1).max(2000),
  severity: z.enum(['info', 'warning', 'critical']).default('info'),
  organizationId: z.string().max(100).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
})

export type AdminListNotificationsInput = z.infer<typeof adminListNotificationsSchema>
export type CreateNotificationInput = z.infer<typeof createNotificationSchema>

// ============================================================================
// FFIEC Billing Schemas
// ============================================================================

export const enableFfiecCanarySchema = z.object({ canaryId: z.string().uuid() })
export const disableFfiecCanarySchema = z.object({ canaryId: z.string().uuid() })

export type EnableFfiecCanaryInput = z.infer<typeof enableFfiecCanarySchema>
export type DisableFfiecCanaryInput = z.infer<typeof disableFfiecCanarySchema>

// ============================================================================
// Infrastructure Schemas
// ============================================================================

/**
 * Email transport provider selector. Derived from the EMAIL_PROVIDERS tuple so
 * validation stays in sync with the single source of truth in constants.
 */
export const emailProviderSchema = z.enum(EMAIL_PROVIDERS)

/**
 * Test Email request. The optional `provider` overrides the active provider for
 * this single diagnostic send.
 */
export const sendTestEmailSchema = z.object({
  email: emailSchema,
  provider: emailProviderSchema.optional(),
})

/**
 * Set the Platform-wide email provider (PUT /api/v1/system-settings/email-provider).
 * Reuses emailProviderSchema so the accepted values stay in sync with the single
 * source of truth (EMAIL_PROVIDERS) rather than redeclaring the enum.
 */
export const updateEmailProviderSettingSchema = z.object({
  provider: emailProviderSchema,
})

export type EmailProviderInput = z.infer<typeof emailProviderSchema>
export type SendTestEmailInput = z.infer<typeof sendTestEmailSchema>
export type UpdateEmailProviderSettingInput = z.infer<typeof updateEmailProviderSettingSchema>

// ============================================================================
// Email Test Harness Schemas
// ============================================================================

/**
 * Registered email template id. Derived from the registry's id tuple so the
 * accepted values stay in sync with the single source of truth (EMAIL_TEMPLATE_IDS).
 */
export const emailTemplateIdSchema = z.enum(EMAIL_TEMPLATE_IDS)

/**
 * Preview an email template. With no `recordId` (or for a `dataSource: 'none'`
 * template) the registry sample data is rendered; otherwise the named record
 * hydrates the body. `recordType` documents which kind of record `recordId` names.
 */
export const emailTemplatePreviewSchema = z.object({
  recordType: z.enum(['organization', 'detection', 'user']).optional(),
  recordId: uuidSchema.optional(),
})

/**
 * Send a test render of an email template. `email` is the ONLY recipient — the
 * resolved record populates the body, never the recipient list. `provider`
 * overrides the active provider for this single diagnostic send.
 */
export const emailTemplateTestSendSchema = z.object({
  email: emailSchema,
  provider: emailProviderSchema.optional(),
  recordType: z.enum(['organization', 'detection', 'user']).optional(),
  recordId: uuidSchema.optional(),
})

export type EmailTemplateIdInput = z.infer<typeof emailTemplateIdSchema>
export type EmailTemplatePreviewInput = z.infer<typeof emailTemplatePreviewSchema>
export type EmailTemplateTestSendInput = z.infer<typeof emailTemplateTestSendSchema>

// ============================================================================
// Billing Type Exports
// ============================================================================

export type CreateCheckoutSessionInput = z.infer<typeof createCheckoutSessionSchema>
export type CreatePortalSessionInput = z.infer<typeof createPortalSessionSchema>
export type UpdatePlanAddonsInput = z.infer<typeof updatePlanAddonsSchema>
export type AddInvoiceRecipientInput = z.infer<typeof addInvoiceRecipientSchema>
export type UsageQueryInput = z.infer<typeof usageQuerySchema>
export type AcceptTermsInput = z.infer<typeof acceptTermsSchema>
export type UpdateUserProfileInput = z.infer<typeof updateUserProfileSchema>
export type CompleteOnboardingInput = z.infer<typeof completeOnboardingSchema>
